import { useDisplayTimezone } from '@/hooks/useDisplayTimezone';
import { calendarDateIn, todayIn } from '@/lib/time';

export interface DateValueLabelProps {
  value: string | null | undefined;
  fontSize?: 'xs' | 'table' | 'sm' | 'base' | 'lg';
  className?: string;
  /** Enable CSS truncation with ellipsis and title tooltip */
  truncate?: boolean;
}

const fontSizeClasses = {
  xs: 'text-xs',
  table: 'text-[13px]',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
};

export const DateValueLabel: React.FC<DateValueLabelProps> = ({
  value,
  fontSize = 'sm',
  className = '',
  truncate,
}) => {
  const timeZone = useDisplayTimezone();
  const sizeClass = fontSizeClasses[fontSize];
  const truncateClasses = truncate
    ? 'block overflow-hidden text-ellipsis whitespace-nowrap'
    : '';

  if (!value) {
    return <span className={`${sizeClass} ${className}`}>—</span>;
  }

  try {
    // Validate ISO 8601 date format (YYYY-MM-DD)
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return (
        <span
          className={`${sizeClass} ${truncateClasses} ${className}`}
          title={truncate ? value : undefined}
        >
          {value}
        </span>
      );
    }

    const [year, month, day] = value.split('-').map(Number);
    const utcDate = new Date(Date.UTC(year, month - 1, day));

    // Rollover guard: invalid dates like 2024-13-45 must fall through to rendering the raw value
    if (
      isNaN(utcDate.getTime()) ||
      utcDate.getUTCFullYear() !== year ||
      utcDate.getUTCMonth() !== month - 1 ||
      utcDate.getUTCDate() !== day
    ) {
      return (
        <span
          className={`${sizeClass} ${truncateClasses} ${className}`}
          title={truncate ? value : undefined}
        >
          {value}
        </span>
      );
    }

    const formatted = new Intl.DateTimeFormat(undefined, {
      timeZone: 'UTC',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(utcDate);

    const todayStr = todayIn(timeZone);
    const [ty, tm, td] = todayStr.split('-').map(Number);
    const yesterdayStr = calendarDateIn(
      new Date(Date.UTC(ty, tm - 1, td - 1)),
      'UTC'
    );
    const tomorrowStr = calendarDateIn(
      new Date(Date.UTC(ty, tm - 1, td + 1)),
      'UTC'
    );

    let relativeLabel = '';
    if (value === todayStr) {
      relativeLabel = 'today';
    } else if (value === yesterdayStr) {
      relativeLabel = 'yesterday';
    } else if (value === tomorrowStr) {
      relativeLabel = 'tomorrow';
    }

    return (
      <span
        className={`${sizeClass} ${truncateClasses} ${className}`}
        title={truncate ? formatted : undefined}
      >
        {formatted}
        {relativeLabel && (
          <span className='text-tertiary'> ({relativeLabel})</span>
        )}
      </span>
    );
  } catch {
    return (
      <span
        className={`${sizeClass} ${truncateClasses} ${className}`}
        title={truncate ? value : undefined}
      >
        {value}
      </span>
    );
  }
};
